import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Alert } from 'react-bootstrap';
import api from "../../services/api";
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import { toast } from 'react-toastify';
import styles from './TrendsManagement.module.scss';

export default function TrendsManagement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIncludeModal, setShowIncludeModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/trends/users');
      setUsers(response.data);
    } catch (error) {
      toast.error(t('admin.trendsManagement.loadError'));
      console.error('Error loading users with trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetTrends = (user) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const handleIncludeAll = (user) => {
    setSelectedUser(user);
    setShowIncludeModal(true);
  };

  const confirmReset = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setShowConfirmModal(false);

    try {
      await api.post(`/settings/admin/trends/user/${selectedUser.userUid}/reset`);
      toast.success(t('admin.trendsManagement.resetSuccess'));
      await loadUsers();
    } catch (error) {
      toast.error(t('admin.trendsManagement.resetError'));
      console.error('Error resetting trends:', error);
    } finally {
      setProcessing(false);
      setSelectedUser(null);
    }
  };

  const confirmIncludeAll = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setShowIncludeModal(false);

    try {
      const response = await api.post(`/settings/admin/trends/user/${selectedUser.userUid}/include-all`);
      const count = response.data?.includedCount ?? 0;
      toast.success(t('admin.trendsManagement.includeAllSuccess', { count }));
      await loadUsers();
    } catch (error) {
      toast.error(t('admin.trendsManagement.includeAllError'));
      console.error('Error including all records:', error);
    } finally {
      setProcessing(false);
      setSelectedUser(null);
    }
  };

  const columns = useMemo(() => [
    {
      header: t('admin.trendsManagement.userName'),
      accessorKey: 'userFullName',
      cell: info => info.getValue() || '-',
    },
    {
      header: t('admin.trendsManagement.includedRecords'),
      accessorKey: 'includedRecordsCount',
      cell: info => info.getValue(),
    },
    {
      header: t('admin.trendsManagement.qualifyingRecords'),
      accessorKey: 'qualifyingRecordsCount',
      cell: info => info.getValue(),
    },
    {
      header: t('admin.trendsManagement.computedTrends'),
      accessorKey: 'computedTrendsCount',
      cell: info => info.getValue(),
    },
    {
      header: t('admin.trendsManagement.actions'),
      id: 'actions',
      cell: ({ row }) => {
        const user = row.original;
        const allIncluded = user.includedRecordsCount >= user.qualifyingRecordsCount;
        return (
          <div className={styles.actionsCell}>
            <Button
              variant="success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleIncludeAll(user);
              }}
              disabled={processing || allIncluded}
            >
              {t('admin.trendsManagement.includeAll')}
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleResetTrends(user);
              }}
              disabled={processing}
            >
              {t('admin.trendsManagement.resetTrends')}
            </Button>
          </div>
        );
      },
    },
  ], [t, processing]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('admin.trendsManagement.title')}</h3>
        <Button variant="outline-primary" onClick={loadUsers}>
          {t('admin.trendsManagement.refresh')}
        </Button>
      </div>

      {users.length === 0 ? (
        <div className={styles.emptyState}>
          <Alert variant="info">
            {t('admin.trendsManagement.noUsers')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <TableFull
            data={users}
            columns={columns}
            styles={styles}
            allowColumnHeaderSort={true}
            allowGlobalFilter={true}
            initialStateSettings={{
              pagination: {
                pageSize: 20,
              },
              sorting: [
                {
                  id: 'userFullName',
                  desc: false,
                },
              ],
            }}
            mainTableClassName={styles.table}
          />
        </div>
      )}

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('admin.trendsManagement.confirmReset')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalContent}>
          {selectedUser && (
            <div className={styles.userInfo}>
              <p>
                <strong>{t('admin.trendsManagement.userName')}:</strong> {selectedUser.userFullName}
              </p>
              <p>
                <strong>{t('admin.trendsManagement.includedRecords')}:</strong> {selectedUser.includedRecordsCount}
              </p>
              <p>
                <strong>{t('admin.trendsManagement.computedTrends')}:</strong> {selectedUser.computedTrendsCount}
              </p>
              <Alert variant="warning">
                {t('admin.trendsManagement.warningReset')}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="warning" onClick={confirmReset}>
            {t('common.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showIncludeModal} onHide={() => setShowIncludeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('admin.trendsManagement.confirmIncludeAll')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalContent}>
          {selectedUser && (
            <div className={styles.userInfo}>
              <p>
                <strong>{t('admin.trendsManagement.userName')}:</strong> {selectedUser.userFullName}
              </p>
              <p>
                <strong>{t('admin.trendsManagement.qualifyingRecords')}:</strong> {selectedUser.qualifyingRecordsCount}
              </p>
              <p>
                <strong>{t('admin.trendsManagement.includedRecords')}:</strong> {selectedUser.includedRecordsCount}
              </p>
              <Alert variant="info">
                {t('admin.trendsManagement.warningIncludeAll')}
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIncludeModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="success" onClick={confirmIncludeAll}>
            {t('common.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
